import { useState, useCallback, useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppSidebar,
  WorkspaceSidebar,
  NewWorkspaceDialog,
  ConfirmDialog,
  useTheme,
} from '@deckspace/ui';
import { useUserPreferences, useAuth } from '@deckspace/client';
import { useApi } from '../lib/api-context';
import {
  createWorkspace,
  deleteWorkspace,
  renameWorkspace,
  fetchWorkspaceDetail,
} from '../lib/queries';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { queryKeys } from '../lib/queryKeys';
import { TitleBar } from '../components/TitleBar';

export interface AppLayoutOutletContext {
  onOpenNewWorkspace: () => void;
  onDeleteWorkspace: (id: string) => void;
  onRenameWorkspace: (id: string) => void;
}

/** Height of the title bar in pixels — kept in sync with TitleBar's h-10 (40px) */
const TITLE_BAR_HEIGHT = 40;

/**
 * Layout wrapper for workspace views with persistent sidebars.
 * Uses React Router's Outlet to render child routes.
 *
 * Structure:
 *   ┌─────────────────────────────────────────┐
 *   │  TitleBar (full width, 40px)            │
 *   ├──────┬──────────────────────────────────┤
 *   │ App  │                                   │
 *   │ Side │  Main content (Outlet)            │
 *   │ bar  │                                   │
 *   └──────┴──────────────────────────────────┘
 */
function AppLayoutInner() {
  const navigate = useNavigate();
  const { id: activeWorkspaceId } = useParams<{ id: string }>();
  const [workspaceSidebarOpen, setWorkspaceSidebarOpen] = useState(false);
  const { colorScheme, setColorScheme } = useTheme();
  const { user, signOut } = useAuth();
  const api = useApi();
  const { preferences } = useUserPreferences(user ? { userId: user.id } : null);

  // Sync server preferences → useTheme
  useEffect(() => {
    if (preferences.colorScheme !== colorScheme) {
      setColorScheme(preferences.colorScheme);
    }
  }, [preferences.colorScheme]);

  const queryClient = useQueryClient();
  const { workspaces, error } = useWorkspaces(api);

  // New workspace dialog state
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false);
  const [newWorkspaceLoading, setNewWorkspaceLoading] = useState(false);
  const [newWorkspaceError, setNewWorkspaceError] = useState<string | null>(null);

  // Delete workspace confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Derive active workspace name from the list
  const activeWorkspaceName = useMemo(() => {
    if (!activeWorkspaceId) return null;
    const ws = workspaces.find((w) => w.id === activeWorkspaceId);
    return ws?.name || null;
  }, [activeWorkspaceId, workspaces]);

  // Handle workspace selection from sidebar
  const handleSelectWorkspace = useCallback(
    (id: string) => {
      navigate(`/w/${id}`);
      setWorkspaceSidebarOpen(false);
    },
    [navigate]
  );

  // Create workspace
  const handleCreateWorkspace = useCallback(() => {
    setNewWorkspaceError(null);
    setNewWorkspaceOpen(true);
  }, []);

  const handleCreateLocal = useCallback(
    async (name: string) => {
      setNewWorkspaceLoading(true);
      setNewWorkspaceError(null);
      try {
        const { id } = await createWorkspace(api, name);
        setNewWorkspaceOpen(false);
        queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
        navigate(`/w/${id}`);
      } catch (err) {
        setNewWorkspaceError(err instanceof Error ? err.message : 'Failed to create workspace');
      } finally {
        setNewWorkspaceLoading(false);
      }
    },
    [api, navigate, queryClient]
  );

  const handleJoinCloud = useCallback((_inviteLink: string) => {
    // TODO: Implement join via invite link
    setNewWorkspaceError('Join via invite link is not yet implemented');
  }, []);

  // Navigate home
  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Settings click
  const handleSettingsClick = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  // Delete workspace
  const handleDeleteWorkspace = useCallback(
    (id: string) => {
      const ws = workspaces.find((w) => w.id === id);
      setDeleteConfirm({
        id,
        name: ws?.name || 'Untitled workspace',
      });
    },
    [workspaces]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm || isDeleting) return;
    setIsDeleting(true);

    try {
      await deleteWorkspace(api, deleteConfirm.id);
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      // If we deleted the active workspace, go home
      if (deleteConfirm.id === activeWorkspaceId) {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to delete workspace:', err);
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  }, [api, deleteConfirm, isDeleting, activeWorkspaceId, navigate, queryClient]);

  const handleDeleteCancel = useCallback(() => {
    if (!isDeleting) setDeleteConfirm(null);
  }, [isDeleting]);

  // Rename workspace
  const handleRenameWorkspace = useCallback(
    async (id: string) => {
      const ws = workspaces.find((w) => w.id === id);
      const currentName = ws?.name || '';
      const newName = window.prompt('Rename workspace:', currentName);
      if (newName === null || newName.trim() === '') return;

      try {
        await renameWorkspace(api, id, newName.trim());
        queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      } catch (err) {
        console.error('Failed to rename workspace:', err);
      }
    },
    [api, workspaces, queryClient]
  );

  // Export workspace
  const handleExportWorkspace = useCallback(
    async (id: string) => {
      try {
        const detail = await fetchWorkspaceDetail(api, id);
        if (!detail) return;

        // Convert Map to plain object for JSON serialization
        const exportData = {
          ...detail,
          datasetRegistry: Object.fromEntries(detail.datasetRegistry),
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `workspace-${detail.name || detail.id}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Failed to export workspace:', err);
      }
    },
    [api]
  );

  // Toggle workspace sidebar
  const handleWorkspaceSidebarToggle = useCallback(() => {
    setWorkspaceSidebarOpen((prev) => !prev);
  }, []);

  // Close workspace sidebar
  const handleWorkspaceSidebarClose = useCallback(() => {
    setWorkspaceSidebarOpen(false);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {/* Full-width title bar */}
      <TitleBar
        workspaceName={activeWorkspaceName}
        user={user}
        onSignOut={signOut}
        onSettingsClick={handleSettingsClick}
        onHomeClick={handleHomeClick}
      />

      {/* Below title bar: sidebar + content */}
      <div className="flex flex-1 min-h-0">
        {/* Narrow icon-only sidebar */}
        <AppSidebar
          workspaceSidebarOpen={workspaceSidebarOpen}
          onWorkspaceSidebarToggle={handleWorkspaceSidebarToggle}
          onCreateWorkspace={handleCreateWorkspace}
          onHomeClick={handleHomeClick}
          onSettingsClick={handleSettingsClick}
          onSignOut={signOut}
          userEmail={user?.email}
        />

        {/* Slide-out workspace list sidebar */}
        <WorkspaceSidebar
          isOpen={workspaceSidebarOpen}
          onClose={handleWorkspaceSidebarClose}
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId || null}
          onSelectWorkspace={handleSelectWorkspace}
          onDeleteWorkspace={handleDeleteWorkspace}
          onRenameWorkspace={handleRenameWorkspace}
          onExportWorkspace={handleExportWorkspace}
          topOffset={TITLE_BAR_HEIGHT}
        />

        {/* Main content area - child routes render here */}
        <main className="flex-1 overflow-hidden bg-background relative">
          {error && (
            <div className="flex h-full items-center justify-center text-destructive">
              <div className="text-center">
                <p className="text-sm font-medium">Failed to load workspaces</p>
                <p className="mt-1 text-xs text-muted-foreground">{error.message}</p>
              </div>
            </div>
          )}
          {!error && (
            <Outlet
              key={activeWorkspaceId}
              context={
                {
                  onOpenNewWorkspace: handleCreateWorkspace,
                  onDeleteWorkspace: handleDeleteWorkspace,
                  onRenameWorkspace: handleRenameWorkspace,
                } satisfies AppLayoutOutletContext
              }
            />
          )}
        </main>
      </div>

      {/* New workspace dialog */}
      <NewWorkspaceDialog
        open={newWorkspaceOpen}
        onClose={() => setNewWorkspaceOpen(false)}
        onCreateLocal={handleCreateLocal}
        onJoinCloud={handleJoinCloud}
        loading={newWorkspaceLoading}
        error={newWorkspaceError}
      />

      {/* Delete workspace confirmation */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Workspace"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? All visualizations, tables, and data will be permanently removed.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        variant="danger"
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}

export { AppLayoutInner as AppLayout };
